import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Layers, ArrowRight } from "lucide-react";
import { SPREADS } from "@/data/spreads";

const scrollTo = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Tiny diagram preview for spread tile
const MiniDiagram = ({ positions, aspect = "2/1" }) => (
  <div className="relative w-full rounded-lg overflow-hidden" style={{ aspectRatio: aspect, background: "linear-gradient(135deg,#131a48,#1B2563)" }}>
    {positions.map((p, i) => (
      <div
        key={i}
        className="absolute"
        style={{
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: positions.length > 8 ? "8%" : "10%",
          aspectRatio: "2/3",
          background: "linear-gradient(180deg,#e6d6f5,#c9b4e8)",
          border: "1px solid rgba(255,255,255,0.85)",
          borderRadius: "3px",
          transform: `translate(-50%, -50%) rotate(${p.rotate || 0}deg)`,
        }}
      />
    ))}
  </div>
);

const SpreadsSection = ({ onSpreadClick }) => {
  return (
    <section id="spreads" className="relative section section-light" data-testid="spreads-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>The Layouts</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif-display text-5xl md:text-6xl leading-tight"
            style={{ color: "var(--cs-purple)" }}
            data-testid="spreads-heading"
          >
            Tarot <span className="italic">Spreads</span>
          </motion.h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: "#475569" }}>
            Each layout is a map of consciousness — a sacred geometry that reveals different dimensions of your question.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SPREADS.map((s, i) => (
            <motion.button
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              onClick={() => onSpreadClick(s)}
              className="box-purple p-6 text-left hover:-translate-y-1 transition-all group"
              style={{ boxShadow: "0 8px 28px rgba(153,76,153,0.25)" }}
              data-testid={`spread-card-${s.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}>
                  <Layers className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <span className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.85)" }}>{s.cards} cards</span>
              </div>

              <h3 className="font-serif-display text-2xl text-white mb-3">{s.name}</h3>

              {/* Mini diagram preview */}
              <div className="mb-4">
                <MiniDiagram positions={s.positions} aspect={s.aspect} />
              </div>

              <p className="text-sm leading-relaxed mb-4" style={{ color: "rgba(255,255,255,0.85)" }}>{s.overview}</p>
              <span className="inline-flex items-center gap-2 text-sm group-hover:gap-3 transition-all" style={{ color: "#FFE9B0" }}>
                Explore spread <ArrowRight className="w-4 h-4" />
              </span>
            </motion.button>
          ))}
        </div>

        <div className="text-center mt-14">
          <button onClick={scrollTo("contact")} className="btn-pill-primary" data-testid="spreads-cta-book">
            <Sparkles className="w-4 h-4" /> Book a Reading with Verna
          </button>
        </div>
      </div>
    </section>
  );
};

export default SpreadsSection;
